import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { Text } from '@reservamos/elements';
import useWhitelabelCopies from 'hooks/whitelabel/useWhitelabelCopies';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import isCdsFrequentPassengerEnabled from 'utils/isCdsFrequentPassengerEnabled';
import isCdsFrequentPassengerBootstrapConfirmedAnonymous from 'utils/isCdsFrequentPassengerBootstrapConfirmedAnonymous';
import { cdsFpOptInToggledTracker } from 'metrics/user-analytics/cdsFrequentPassenger';
import { useCdsFrequentPassenger } from '../context/CdsFrequentPassenger';
import PassengerPolicies from './purchase/PassengerPolicies';
import CheckboxRenderer from './CheckboxRenderer';

/**
 * Wraps CheckboxRenderer to track the CDS Frequent Passenger opt-in toggle.
 * Reads the pre-click `input.checked` (current stored value) to derive the
 * post-click value, rather than the DOM event target, since a checkbox click
 * always flips the boolean.
 * @param {object} props
 * @param {object} props.input - redux-form input props.
 * @returns {React.ReactElement}
 */
function CdsFrequentPassengerOptInCheckbox(props) {
  const { input } = props;

  return (
    <CheckboxRenderer
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      onClick={() => cdsFpOptInToggledTracker(!input.checked)}
    />
  );
}

CdsFrequentPassengerOptInCheckbox.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  input: PropTypes.object.isRequired,
};

function AgreementCheckboxFields() {
  const { t } = useTranslation('purchase');
  const { provider } = useWhitelabelCopies();
  const { SHOW_PROMOTIONAL_TERMS_CHECKBOX, SHOW_LEGAL_TERMS_CHECKBOX, ...features } =
    useWhitelabelFeatures();
  const { brand } = useWhitelabelEnvs();
  const { isAuthenticated, isBootstrapped, error } = useCdsFrequentPassenger();

  const showCdsFrequentPassengerOptIn =
    isCdsFrequentPassengerEnabled(features, brand) &&
    isCdsFrequentPassengerBootstrapConfirmedAnonymous({ isBootstrapped, isAuthenticated, error });

  return (
    <form>
      {SHOW_LEGAL_TERMS_CHECKBOX && (
        <Field
          name="legalTerms"
          id="legalTermsCheckbox"
          type="checkbox"
          label={<PassengerPolicies />}
          component={CheckboxRenderer}
        />
      )}
      {SHOW_PROMOTIONAL_TERMS_CHECKBOX && (
        <Field
          name="promotionalTerms"
          id="promotionalTermsCheckbox"
          type="checkbox"
          label={<Text size="S">{t('text.promotional_terms', { provider: provider.name })}</Text>}
          component={CheckboxRenderer}
        />
      )}
      {showCdsFrequentPassengerOptIn && (
        <Field
          name="cdsFrequentPassengerOptIn"
          id="cdsFrequentPassengerOptInCheckbox"
          type="checkbox"
          label={<Text size="S">{t('text.cds_frequent_passenger_opt_in')}</Text>}
          component={CdsFrequentPassengerOptInCheckbox}
        />
      )}
    </form>
  );
}

export default reduxForm({
  form: 'purchaser',
})(AgreementCheckboxFields);
